import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { api } from './api';
import { DEFAULT_NOTIFICATION_SOUNDS, type NotificationSettingsDto } from '@dijitalapp/shared';
import { mergeSounds, setSoundPrefs, setTitleTexts, setWorkerTexts } from './notify';
import { useApp } from './app-context';
import { ChatStore, type ChatState } from './chat-store';

const Ctx = createContext<ChatStore | null>(null);

/** One chat store per signed-in account; torn down on sign-out or account switch. */
export function ChatProvider({ children }: { children: ReactNode }) {
  const { me, config, setMe, i18n } = useApp();
  const enabled = Boolean(me && config?.flags.messaging);
  const userId = enabled ? me!.id : null;
  const [store, setStore] = useState<ChatStore | null>(null);

  useEffect(() => {
    if (!userId) return;
    const s = new ChatStore(userId, () => setMe(null));
    // Publishing the store from the effect is the subscription to this external system.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStore(s);
    void s.start();
    return () => {
      s.stop();
      setStore(null);
    };
  }, [userId, setMe]);
  // Admin switch: new direct chats switch to end-to-end encryption on their first message.
  useEffect(() => {
    store?.setAutoEncrypt(Boolean(config?.flags.e2ee_by_default));
  }, [store, config]);

  // Alert texts in the person's language, and how much a notification may show (their setting).
  useEffect(() => {
    if (!store) return;
    const { t } = i18n;
    // What a notification says of a message's kind ("🎥 Video (0:12)", "📄 rapor.pdf"), here and in the worker.
    const previewTexts = {
      newMessage: t('push.newMessage'),
      photo: t('push.photo'),
      video: t('push.videoClip'),
      voice: t('push.voice'),
      file: t('push.file'),
      document: t('push.document'),
      gif: t('push.gif'),
      sticker: t('push.sticker'),
      location: t('push.location'),
      contact: t('push.contact'),
      poll: t('push.poll'),
      attachment: t('push.attachment'),
    };
    store.setAlertTexts(
      {
        newMessage: t('push.newMessage'),
        newMessageHidden: t('push.newMessageHidden'),
        newFrom: (name) => t('notify.title.newFrom', { name }),
        brand: 'DijitalApp',
        mentioned: t('push.mentioned'),
        chatUnread: (count) => t('push.chatUnread', { count }),
        preview: previewTexts,
      },
      {
        incomingVoice: (name) => t('call.incomingVoice', { name }),
        incomingVideo: (name) => t('call.incomingVideo', { name }),
        tapToAnswer: t('notify.tapToAnswer'),
        missedFrom: (name) => t('notify.title.missedFrom', { name }),
      },
      {
        ringing: (group, name) => t('gcall.ringing', { group, name }),
        tapToJoin: t('gcall.tapToJoin'),
      },
    );
    setTitleTexts({
      unread: (count) => t('notify.title.unread', { count }),
      missed: (count) => t('notify.title.missed', { count }),
    });
    // The service worker's buttons on a push (Reply / Mark as read) and the words of encrypted previews, in the same
    // language (public/sw-preview.js words them on the device).
    setWorkerTexts({
      reply: t('push.reply'),
      markRead: t('push.markRead'),
      replyPlaceholder: t('push.replyPlaceholder'),
      replyFailed: t('push.replyFailed'),
      mentioned: t('push.mentioned'),
      preview: { ...previewTexts },
    });
    void api<NotificationSettingsDto>('GET', '/me/notifications').then(
      (r) => {
        store.setNotificationPreview(r.preview);
        // The person's sounds (an older server sends none: the defaults).
        setSoundPrefs(mergeSounds(DEFAULT_NOTIFICATION_SOUNDS, r.sounds));
      },
      () => undefined,
    );
  }, [store, i18n]);

  return <Ctx.Provider value={store}>{children}</Ctx.Provider>;
}

export function useChatStore(): ChatStore | null {
  return useContext(Ctx);
}

const EMPTY: ChatState = {
  conversations: [],
  loaded: false,
  messages: {},
  hasMore: {},
  pending: {},
  typing: {},
  presence: {},
  connection: 'connecting',
  starred: new Set<string>(),
  reminders: new Map<string, string>(),
  remindersVersion: 0,
  e2eeReady: false,
  e2eeFailed: null,
  profileVersion: 0,
  e2eeVersion: 0,
  plannerVersion: 0,
  callsVersion: 0,
  learnVersion: 0,
  storiesVersion: 0,
  folders: [],
  scheduledVersion: 0,
};
const noopSubscribe = () => () => undefined;

export function useChat(): ChatState {
  const store = useChatStore();
  return useSyncExternalStore(store?.subscribe ?? noopSubscribe, store ? store.getSnapshot : () => EMPTY);
}
